import React, { useEffect } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useQuery } from "react-query";
import fetchShiftJobPunches from "api/jobs/fetchShiftJobPunches";

const DeleteShiftConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  jobId,
  shiftData,
  shiftName = "",
}) => {
  const {
    data: shiftPunches = [],
    isLoading: isLoadingPunches,
    refetch: refetchPunches,
  } = useQuery(
    ["shiftJobPunches", { jobId, shiftSlug: shiftData?.slug }],
    () => fetchShiftJobPunches({ jobId, shiftSlug: shiftData?.slug }),
    {
      enabled: !!open && !!jobId && !!shiftData?.slug,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    // When the dialog opens and we have valid data, fetch punches
    if (open && jobId && shiftData?.slug) {
      refetchPunches();
    }
  }, [open, jobId, shiftData, refetchPunches]);

  const hasPunches = shiftPunches?.length > 0;
  const displayName = shiftName || shiftData?.shiftName || "";

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="delete-confirm-dialog-title">
      <DialogTitle id="delete-confirm-dialog-title">Confirm Shift Deletion</DialogTitle>
      <DialogContent>
        {(() => {
          if (isLoadingPunches) {
            return <Typography>Checking if this shift has time punches...</Typography>;
          }

          if (hasPunches) {
            const punchText = shiftPunches.length === 1 ? "punch" : "punches";
            return (
              <Typography>
                This shift {displayName ? `"${displayName}"` : ""} cannot be deleted because it has{" "}
                {shiftPunches.length} {punchText} recorded. You must delete all time punches for
                this shift before it can be removed.
              </Typography>
            );
          }

          return (
            <Typography>
              Are you sure you want to delete this shift{displayName ? ` "${displayName}"` : ""}?
              This action cannot be undone.
            </Typography>
          );
        })()}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          color="error"
          autoFocus
          disabled={hasPunches || isLoadingPunches}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

DeleteShiftConfirmationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  jobId: PropTypes.string,
  shiftData: PropTypes.object,
  shiftName: PropTypes.string,
};

DeleteShiftConfirmationDialog.defaultProps = {
  jobId: "",
  shiftData: null,
  shiftName: "",
};

export default DeleteShiftConfirmationDialog;
